(function () {
  angular.module('app.main').factory('mainBoot', getService);

  /* @ngInject */
  function getService(
    $injector,
    $location,
    $log,
    $rootScope,
    $state,
    $timeout,
    $translate,
    _,
    appReady,
    AUTH_EVENTS,
    authentication,
    AUTHENTICATION_READY_ERROR,
    BlackoutError,
    CurtainNotifications,
    feature,
    invitesStorage,
    IS_IE11_BROWSER,
    PreLoginRouterState,
    Redirector,
    STORAGE_AVAILABLE,
    translationUtils
  ) {
    let allowStateTransitions;
    const service = {
      makeReady,
    };

    return service;

    function makeReady(...services) {
      // $rootScope.appReady is true when all following 3 conditions are fulfilled:
      // - user is logged in (IMS)
      // - user profile loaded (IMS)
      // - user is authorized on RC (JIL)
      // - language file loaded
      // Passed to $rootScope so it's accessible in views in order to show/hide various components.
      // This became a requirement when site elements that are visible everywhere (e.g. footer) started requiring a language pack to be loaded.
      $rootScope.appReady = false;

      if (STORAGE_AVAILABLE) {
        /* === Critical dependencies ===*/
        // Block state transitions until critical dependencies are satisfied.
        // The blocking $stateChangeStart handler below gets removed after all dependencies are loaded.
        allowStateTransitions = $rootScope.$on(
          '$stateChangeStart',
          (event, toState, toStateParams, fromState) => {
            // Only allow transitions from the "default" ui-router state to our root state.
            // This is needed because the "default" ui-router state, named `''`, is not in the list
            // of defined states (and cannot be), thus triggering the `otherwise()` rule which redirects
            // all unknown URLs to the root state.
            // If we were to prevent this redirect (from `''` to `root`), an infinite loop would start
            // as the router is trying to resolve the unknown URL via the otherwise-rule.
            if (fromState.name !== '' || toState.name !== 'root') {
              event.preventDefault();

              // Save requested URL to redirect to after app is ready (if the case)
              PreLoginRouterState.set(toState.name, toStateParams);
            }
          }
        );

        appReady
          .run(...services)
          .then(onAppReady)
          .catch(onAppReadyError);
      } else {
        // Session Storage not available
        authFlowFinished(true);
        Redirector.go('cookiesDisabled', true);
      }
    }

    ////////////

    function onAppReady() {
      const Auth = $injector.get('Auth');
      const BreadcrumbsMenu = $injector.get('BreadcrumbsMenu');
      const DemoMode = $injector.get('DemoMode');
      const RcUserRoles = $injector.get('RcUserRoles');
      const User = $injector.get('User');
      const OrganizationList = $injector.get('OrganizationList');

      /* === Handlers for $state events ===*/
      // These need to be instantiated after load-time deps finish because ui-router's preventDefault() does not stop event propagation
      // and so all listener methods of $stateChangeStart would fire earlier than they should.
      function initStateEventListeners() {
        /* === Basic route access restrictions ===*/
        // eslint-disable-next-line angular/on-watch, complexity
        $rootScope.$on('$stateChangeStart', (event, toState, toStateParams) => {
          let redirectToState;

          // All states must have access information
          if (toState.data && toState.data.access) {
            const isLoggedIn = Auth.isLoggedIn();
            const authInProgress = Auth.inProgress();

            // User is not authorized to access next state
            if (Auth.authorize(toState.data.access)) {
              // User is authorized to access next state
              // If app is ready, redirect user:
              // - from root or login page to default page (based on user role) when logged in
              // - from root to login page when logged out
              if ($rootScope.appReady) {
                // Logged in users go to their default route according to user role
                if ((toState.name === 'root' || toState.name === 'login') && isLoggedIn) {
                  redirectToState = RcUserRoles.getStateNameByRole(User.getRole());
                } else if (toState.name === 'root' && !authInProgress && !isLoggedIn) {
                  // Logged out users go to /login
                  redirectToState = 'login';
                }
              }
              // If user is not logged in, save current URL to redirect to after login and redirect to login page
              // Note: auth must not be in progress in order to properly determine user login status
            } else if (!authInProgress && !isLoggedIn) {
              // Save requested URL to redirect to after app is ready (if the case)
              PreLoginRouterState.set(toState.name, toStateParams);
              redirectToState = 'login';
            } else if ($rootScope.appReady) {
              // Otherwise, redirect to default state for current user role
              redirectToState = RcUserRoles.getStateNameByRole(User.getRole());
            } else {
              // User is logged in, but application isn't ready yet, redirect to root until login and resellers are fetched
              // Save requested URL to redirect to after app is ready (if the case)
              PreLoginRouterState.set(toState.name, toStateParams);
              redirectToState = 'root';
            }
          } else {
            // If no route access information is present, logout
            redirectToState = 'logout';
          }

          // Redirect if the current and redirect URLs are different
          if (redirectToState) {
            // Stop the state transition from happening.
            // Otherwise the view and controller will get instantiated.
            event.preventDefault();

            $log.info('%c[$stateChangeStart handler]', 'color:#CCCCCC;');
            Redirector.go(redirectToState, true); // true means replace URL in history
          }
        });

        // Listen for $stateChangeSuccess and apply state specific changes (e.g. header on/off)
        // eslint-disable-next-line angular/on-watch
        $rootScope.$on('$stateChangeSuccess', (event, current) => {
          if (current.data) {
            // Header toggle
            $rootScope.hideHeader = !!current.data.hideHeader;
            $rootScope.hideFooter = !!current.data.hideFooter;

            if (feature.isEnabled('enable-generic-error')) {
              CurtainNotifications.show(
                CurtainNotifications.ERROR,
                'blackoutError.generic.message'
              );
            }
            // Set breadcrumbs menu data for current state
            BreadcrumbsMenu.set(current.data.breadcrumbsMenu);
          } else {
            // No state data property means no breadcrumbs
            BreadcrumbsMenu.clear();
          }

          // Trigger head <title> change
          updateHeadTitle();
        });

        /* === State Change Error Handler ===*/
        // eslint-disable-next-line angular/on-watch
        $rootScope.$on('$stateChangeError', () => {
          $log.info('%c[$stateChangeError handler]', 'color:#CCCCCC;');

          // Go to default user state
          Redirector.go();
        });

        /* === Entry state check ===*/
        // This is a check performed only once during app lifetime to determine
        // if the entry state (i.e. the first state accessed) wants to prevent
        // automatic login by not attempting SSO login right away. The default
        // behaviour is to attempt SSO at app start-up and automatically log in
        // the user (if valid SSO session is found) or request login by redirecting
        // to `login` state.
        // If a state wants to prevent this, it will have a preventAutoLogin:true
        // setting in its data block.
        // Such a state is the `invitation` state. If the user lands on this state
        // auto login is prevented and details about invitations are fetched first.
        const unregisterEntryRouteHandler = $rootScope.$on(
          '$stateChangeStart',
          (event, toState) => {
            // Check only once during app lifetime
            unregisterEntryRouteHandler();

            // Do not trigger auto-login if requested state has preventAutoLogin set to true
            if (toState.data && toState.data.preventAutoLogin === true) {
              // Auth is no longer a concern, proceed with other dependencies
              // Prevent redirect to default route so that the current state isn't navigated away from
              authFlowFinished(true);

              $log.info(
                `%cAutomatic login prevented. %c[${toState.name}]`,
                'font-weight:bold;color:#009966;',
                'color:#CCCCCC;'
              );
            } else if (toState.name !== 'login') {
              // Trigger auto-login
              // We're in the middle of a $state transition.
              // Prevent it and redirect to login instead.
              // No point in cancelling login to go to login
              event.preventDefault();

              // Hack to defeat rarely occuring bug that freezes the app on root state after redirection from IMS,
              // more common after a logout than a login.
              $timeout(() => {
                Redirector.go('login', true);
              }, 0);
            }
          }
        );
      }

      /* === Listen for auth events ===*/
      /* eslint-disable angular/on-watch */
      $rootScope.$on(AUTH_EVENTS.DONE, authFlowFinished);
      $rootScope.$on(AUTH_EVENTS.LOGOUT, requiresLogin);
      /* eslint-enable angular/on-watch */

      // Breadcrumbs menu $watcher for when breadcrumbs are updated outside of the Main Controller, e.g. other controllers
      // eslint-disable-next-line angular/on-watch
      $rootScope.$watch(BreadcrumbsMenu.get, () => {
        $rootScope.breadcrumbsMenu = BreadcrumbsMenu.get();
      });

      // Expose User service to views
      $rootScope.User = User;

      // Expose BlackoutError service to views
      $rootScope.BlackoutError = BlackoutError;

      // Expose browser support constant to views
      $rootScope.IS_IE11_BROWSER = IS_IE11_BROWSER;

      // Expose Demo Mode status to views
      $rootScope.demoModeEnabled = function () {
        return DemoMode.isEnabled();
      };

      // Navigate to dashboard state
      // Placed inside the main controller for convenience as it's needed by most other state controllers
      $rootScope.goToDashboard = function () {
        if (User.isLicenseAdmin()) {
          $state.go('licenseAdmin.impersonatedReseller.dashboard');
        } else {
          $state.go('dashboard');
        }
      };

      // Navigate to dashboard state
      $rootScope.goToRootState = function (event) {
        event.preventDefault();

        if (!BlackoutError.isActive()) {
          $state.go('root');
        }
      };

      /* === Logout handler ===*/
      function requiresLogin() {
        $log.info('%c[Logout handler]', 'color:#CCCCCC;');

        // Clear state (just to be safe)
        $rootScope.appReady = false;
        User.clear();

        // Then redirect to login
        Redirector.go('login', true);
      }

      // HTML head <title>
      function updateHeadTitle() {
        $rootScope.headTitle = `${translationUtils.sanitizeSafeInstant(
          _.get($state, 'current.data.headTitle')
        )} | ${$translate.instant('global.appName')}`;
      }

      updateHeadTitle();

      // Set up an OrganizationList model that doesn't refresh right away via get (since fetching the org list for a
      // reseller doesn't make sense in this context) so apiUtils can access it without it trying to call refresh
      OrganizationList.model = new OrganizationList();

      // Remove the $stateChangeStart handler that calls preventDefault() on any state change
      allowStateTransitions();

      // Init "normal" state event listeners
      initStateEventListeners();

      // Resume entry state transition (i.e. the state the user first accessed, before loading dependencies)
      const stateName = PreLoginRouterState.getName() || 'root';
      const stateParams = PreLoginRouterState.getParams();
      PreLoginRouterState.clear(); // Only valid once

      $state.transitionTo(stateName, stateParams, {reload: true});
    }

    /* === App Ready ===*/
    // Called after auth process is complete and OK.
    function authFlowFinished(preventSuccessRedirect) {
      // Final condition fulfilled, app is ready
      $rootScope.appReady = true;

      if (preventSuccessRedirect !== true) {
        // Calling go() without a path so that user is redirected to the URL requested before logging in or, otherwise, the default path.
        Redirector.go('', true);
      }
    }

    function onAppReadyError(error) {
      switch (error) {
        case AUTHENTICATION_READY_ERROR.USER_SIGNED_OUT:
          // invitations allow logged out actions to occur
          // unless its a cached one, in which case we need the auth token
          if (
            !_.startsWith($location.path(), '/invitation/') ||
            invitesStorage.hasCachedInviteCode()
          ) {
            authentication.signIn();
          }
          break;
        default:
          // the primary difference here is we want to log the cause
          // any of the ones above are expected, and so not necessary to log
          $log.error('Unhandled appReady error: ', error);
          BlackoutError.showAuthorizationFailError();
      }
    }
  }
})();
